<template>
    <!-- Main container for the outbound targets page -->
    <div class="dashboard-container">
        <!-- Sidebar component -->
        <side-bar />
        <div class="main-content">
            <!-- Breadcrumbs for navigation -->
            <div class="container">
                <partner-breadcrumbs />
            </div>
            <!-- Tabs for switching between bots and outbound calling -->
            <div class="tabs">
                <div class="tab-header">
                    <button class="tab-button"
                        @click="$router.push({ path: '/partner/customer', query: { activeTab: 'bots' } })"
                        :class="{ active: activeTab === 'bots' }">AI Agents</button>
                    <button class="tab-button"
                        @click="$router.push({ path: '/partner/customer', query: { activeTab: 'outboundCampaign' } })"
                        :class="{ active: activeTab === 'outboundCampaign' }">Outbound Calling</button>
                </div>
            </div>
            <!-- Wizard steps showing progress -->
            <div class="wizard-steps">
                <div class="completed">
                    <div class="num">1</div> <span class="step">Create Campaign</span>
                    <span class="arrow">&#8594;</span>
                </div>
                <div class="current">
                    <div class="num">2</div> <span class="step">Add Targets</span>
                </div>
            </div>

            <!-- Section for uploading CSV target list -->
            <div class="container upload-section">
                <h3>Upload Target List (.csv)</h3>
                <input type="file" @change="handleCsvUpload" accept=".csv" class="csv-upload-input" />
                <p class="note" v-if="customerGUID != 'AD19537023AE4333B185521BF7078F60'">
                    Your CSV file must include a <strong>Target Number</strong> column. This column is mandatory and
                    phone numbers must be provided in international format, for example: <strong>+61499099099</strong> or <strong>61499099099</strong>.
                    <br><br>
                    You can also include any additional columns such as <strong>Name</strong>, <strong>Company</strong>,
                    <strong>Appointment Date</strong>, or any other information you would like the AI agent to use
                    during the conversation.
                    <br><br>
                    These additional columns will automatically be converted into AI Agent metadata behind the scenes — no
                    JSON formatting is required.
                    <br><br>
                    <strong>Example:</strong>
                    <br>
                    Target Number, Name, Company
                    <br>
                    +61499099099, Jane Doe, ABC Test Company
                    <br><br>
                    Additional columns are optional. If they are not provided, the AI Agent will continue using its default
                    behaviour.
                </p>
                <div v-else>
                    <div class="note" v-if="botName.includes('DB Re-engagement')">
                        <strong>CSV Upload Guidelines</strong>
                        <ul class="guidelines-list">
                            <li>CSV should contain three columns.</li>
                            <li>The required headers are: <strong>Contractor Name</strong>, <strong>Contact
                                    Number</strong>, and <strong>Company Name</strong>.</li>
                            <li>Contact number must be provided in international phone number format.</li>
                            <li>Example: +61499099099.</li>
                        </ul>
                    </div>
                    <div class="note" v-else-if="botName.includes('Expired Documentation')">
                        <strong>CSV Upload Guidelines</strong>
                        <ul class="guidelines-list">
                            <li>CSV should contain four columns.</li>
                            <li>The required headers are: <strong>Contractor Name</strong>, <strong>Contact
                                    Number</strong>, <strong>Document Type</strong>, and <strong>Expiry Date</strong>.
                            </li>
                            <li>Contact number must be provided in international phone number format.</li>
                            <li>Example: +61499099099.</li>
                            <li>Expiry Date must be in dd-MM-yyyy format.</li>
                        </ul>
                    </div>
                    <div class="note" v-else-if="botName.includes('Truck Availability')">
                        <strong>CSV Upload Guidelines</strong>
                        <ul class="guidelines-list">
                            <li>CSV should contain five columns.</li>
                            <li>The required headers are: <strong>Contractor Name</strong>, <strong>Contact
                                    Number</strong>, <strong>Equipment Type</strong>, <strong>Date/Time</strong>, and
                                <strong>Suburbs</strong>.
                            </li>
                            <li>Contact number must be provided in international phone number format.</li>
                            <li>Example: +61499099099.</li>
                            <li>Date/Time must be in dd-MM-yyyy HH:mm format.</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="container" v-if="customerGUID != 'AD19537023AE4333B185521BF7078F60'">
                <!-- Table header for regular targets -->
                <div class="target-table-header">
                    <div class="column">Target Phone Number*</div>
                    <div class="column">Metadata</div>
                    <div class="column column-btn"></div>
                </div>

                <!-- Input rows for regular targets -->
                <div v-for="(target, index) in targets" :key="index" class="target-table-row">
                    <input type="text" v-model="target.targetNumber" placeholder="Enter phone number"
                        class="target-input" />
                    <input type="text" v-model="target.botMetadata" placeholder='{"field1":"val1","field2":"val2"}'
                        class="target-input" />
                    <button @click="removeTarget(index)" class="remove-btn">
                        REMOVE
                    </button>
                </div>

                <!-- Button to add new target row -->
                <div class="table-action-row">
                    <button class="add-round-btn" @click="addTarget" title="Add target">+</button>
                </div>

                <p class="note">Target number must be
                    provided in
                    international phone number format. e.g. +61499099099.<br>Metadata must be
                    provided in valid JSON format,<strong> e.g.{"name": "Jane Doe", "company": "ABCTest
                        Company"}</strong>. The AI Agent can use this metadata to personalise the conversation
                    and guide flows.
                    Providing metadata is <strong>optional</strong> — if it is not supplied, the AI Agent will
                    still
                    function
                    using default behaviour.
                </p>

                <p class="customization-note">
                    For additional options and customizations, please email us at
                    <a href="mailto:support@trimate.ai">support@trimate.ai</a>.
                </p>

                <button class="finish-button" @click="submitTargets">FINISH</button>
            </div>
            <!-- Target form for special customer (fleet for hire) -->
            <div v-else-if="customerGUID === 'AD19537023AE4333B185521BF7078F60'">
                <!-- Table Headers -->
                <div class="container" v-if="botName.includes('DB Re-engagement')">
                    <div class="target-table-header">
                        <div class="column">Contractor Name</div>
                        <div class="column">Contact Number*</div>
                        <div class="column">Company Name</div>
                        <div class="column column-btn"></div>
                    </div>
                    <!-- Input Rows -->
                    <div v-for="(target, index) in specialTargets" :key="index" class="target-table-row">
                        <input type="text" v-model="target.contractorName" placeholder="Enter contractor name"
                            class="target-input special-target-input" />
                        <input type="text" v-model="target.contactNumber" placeholder="Enter phone number"
                            class="target-input" />
                        <input type="text" v-model="target.companyName" placeholder="Enter company name"
                            class="target-input" />
                        <button @click="removeSpecialTarget(index)" class="remove-btn">
                            REMOVE
                        </button>
                    </div>

                    <div class="table-action-row">
                        <button class="add-round-btn" @click="addSpecialTarget" title="Add target">+</button>
                    </div>
                </div>
                <div class="container" v-else-if="botName.includes('Expired Documentation')">
                    <div class="target-table-header">
                        <div class="column">Contractor Name</div>
                        <div class="column">Contact Number*</div>
                        <div class="column">Document Type</div>
                        <div class="column">Expiry Date</div>
                        <div class="column column-btn"></div>
                    </div>
                    <!-- Input Rows -->
                    <div v-for="(target, index) in specialTargets" :key="index" class="target-table-row">
                        <input type="text" v-model="target.contractorName" placeholder="Enter contractor name"
                            class="target-input special-target-input" />
                        <input type="text" v-model="target.contactNumber" placeholder="Enter phone number"
                            class="target-input" />
                        <input type="text" v-model="target.documentType" placeholder="Enter document type"
                            class="target-input" />
                        <input type="date" v-model="target.expiryDate" placeholder="Enter expiry date (DD/MM/YYYY)"
                            class="target-input" />
                        <button @click="removeSpecialTarget(index)" class="remove-btn">
                            REMOVE
                        </button>
                    </div>

                    <div class="table-action-row">
                        <button class="add-round-btn" @click="addSpecialTarget" title="Add target">+</button>
                    </div>
                </div>
                <div class="container" v-else-if="botName.includes('Truck Availability')">
                    <div class="target-table-header">
                        <div class="column">Contractor Name</div>
                        <div class="column">Contact Number*</div>
                        <div class="column">Equipment Type</div>
                        <div class="column">Date/Time</div>
                        <div class="column">Suburbs</div>
                        <div class="column column-btn"></div>
                    </div>
                    <!-- Input Rows -->
                    <div v-for="(target, index) in specialTargets" :key="index" class="target-table-row">
                        <input type="text" v-model="target.contractorName" placeholder="Enter contractor name"
                            class="target-input" />
                        <input type="text" v-model="target.contactNumber" placeholder="Enter phone number"
                            class="target-input" />
                        <input type="text" v-model="target.equipmentType" placeholder="Enter equipment type"
                            class="target-input" />
                        <input type="datetime-local" v-model="target.avlblDateTime" placeholder="dd-MM-yyyy HH:mm"
                            class="target-input" />
                        <input type="text" v-model="target.suburbs" placeholder="Enter suburbs" class="target-input" />
                        <button @click="removeSpecialTarget(index)" class="remove-btn">
                            REMOVE
                        </button>
                    </div>

                    <div class="table-action-row">
                        <button class="add-round-btn" @click="addSpecialTarget" title="Add target">+</button>
                    </div>
                </div>
                <p class="note">Target number must be provided in
                    international phone number format. e.g. +61499099099.</p>
                <p class="customization-note">
                    For additional options and customizations, please email us at
                    <a href="mailto:support@trimate.ai">support@trimate.ai</a>.
                </p>
                <button class="finish-button" @click="submitSpecialTargets">FINISH</button>
            </div>
        </div>
    </div>
</template>

<script>
import SideBar from './SideBar.vue';
import PartnerBreadcrumbs from './PartnerBreadcrumbs.vue';

// This component allows partners to add targets for outbound calling campaigns.
// It supports uploading CSV files or manually entering target details.
// For special customers, it handles specific bot types with custom fields.

export default {
    name: 'OutboundTargets',
    components: {
        SideBar,
        PartnerBreadcrumbs
    },
    data() {
        return {
            activeTab: 'outboundCampaign', // Keeps track of the active tab (outboundCampaign)
            targets: [{ targetNumber: '', botMetadata: '' }], // List of targets for regular customers, each with phone number and metadata
            specialTargets: [{}], // List of targets for special customer (fleet for hire), with custom fields based on bot type
            customerGUID: '', // Unique identifier for the customer
            campaignId: '', // ID of the outbound campaign
            botId: '', // ID of the bot being used
            botName: '', // Display name of the bot
        }
    },
    mounted() {
        // When the component loads, get necessary data from localStorage and URL query parameters
        this.customerGUID = (localStorage.getItem('customerGUID') || '').toUpperCase();
        this.botName = localStorage.getItem('botDisplayName') || '';
        this.botDID = localStorage.getItem('campaignBotDID') || '';
        this.campaignId = this.$route.query.campaignid || '';
        this.botId = this.$route.query.botid || '';
    },
    methods: {
        // This function cleans up and checks if a phone number is valid in international format
        formatAndValidatePhoneNumber(number) {
            if (!number) {
                return null; // If no number provided, it's invalid
            }

            // Remove any characters that are not digits or '+'
            let cleanedNumber = number.replace(/[^\d+]/g, '');

            // If it doesn't start with '+', add it (assuming it's international)
            if (!cleanedNumber.startsWith('+')) {
                cleanedNumber = '+' + cleanedNumber;
            }

            // Check if it matches the pattern: starts with '+' and has 7 to 15 digits
            const internationalPhoneNumberRegex = /^\+\d{7,15}$/;
            return internationalPhoneNumberRegex.test(cleanedNumber) ? cleanedNumber : null;
        },
        // Adds a new empty target row to the list for manual entry
        addTarget() {
            this.targets.push({ targetNumber: '', botMetadata: '' });
        },
        // Removes a target row from the list at the given index
        removeTarget(index) {
            this.targets.splice(index, 1);
        },
        // Handles uploading and processing a CSV file to populate targets
        handleCsvUpload(event) {
            const file = event.target.files[0];
            if (!file || file.type !== 'text/csv') {
                alert("Please upload a valid .csv file.");
                return;
            }

            const reader = new FileReader();
            reader.onload = e => {
                try {
                    // Get the text from the CSV file and clean it up
                    const csvText = e.target.result.trim();
                    const lines = csvText.split('\n').map(line => line.trim()).filter(Boolean);

                    if (lines.length < 2) {
                        alert('CSV file must have a header and at least one data row.');
                        return;
                    }

                    // Get the headers from the first line
                    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                    const invalidNumbers = [];

                    // Check if it's the special customer and handle accordingly
                    if (this.customerGUID === 'AD19537023AE4333B185521BF7078F60'
                        && (this.botName.includes('DB Re-engagement') || this.botName.includes('Expired Documentation') || this.botName.includes('Truck Availability'))) {
                        // Find the index of each required column
                        const contractorNameIndex = headers.indexOf('Contractor Name');
                        const contactNumberIndex = headers.indexOf('Contact Number');
                        const companyNameIndex = headers.indexOf('Company Name');
                        const docTypeIndex = headers.indexOf('Document Type');
                        const expiryDateIndex = headers.indexOf('Expiry Date');
                        const equipmentTypeIndex = headers.indexOf('Equipment Type');
                        const avlblDateTimeIndex = headers.indexOf('Date/Time');
                        const suburbsIndex = headers.indexOf('Suburbs');

                        if (contactNumberIndex === -1) {
                            alert("CSV must contain a 'Contact Number' column.");
                            return;
                        }

                        const newSpecialTargets = [];
                        let rowCounter = 1;
                        // Loop through each data row
                        for (let i = 1; i < lines.length; i++) {
                            rowCounter++;
                            const row = lines[i].split(',');
                            const originalContactNumber = row[contactNumberIndex]?.trim();
                            // Validate and format the phone number
                            const contactNumber = this.formatAndValidatePhoneNumber(originalContactNumber);

                            if (contactNumber === null) {
                                invalidNumbers.push(`Row ${rowCounter}: '${originalContactNumber}'`);
                                continue;
                            }

                            if (originalContactNumber) {
                                // Format expiry date if present
                                let expiryDate = row[expiryDateIndex]?.trim() || '';
                                if (expiryDate) {
                                    const parts = expiryDate.split('-');
                                    if (parts.length === 3) {
                                        const day = parts[0].length === 4 ? parts[2] : parts[0];
                                        const month = parts[1];
                                        const year = parts[0].length === 4 ? parts[0] : parts[2];
                                        expiryDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                                    } else {
                                        const dateObj = new Date(expiryDate);
                                        if (!isNaN(dateObj.getTime())) {
                                            const y = dateObj.getFullYear();
                                            const m = String(dateObj.getMonth() + 1).padStart(2, '0');
                                            const d = String(dateObj.getDate()).padStart(2, '0');
                                            expiryDate = `${y}-${m}-${d}`;
                                        }
                                    }
                                }
                                // Format available date/time if present
                                let avlblDateTime = row[avlblDateTimeIndex]?.trim() || '';
                                if (avlblDateTime) {
                                    const parts = avlblDateTime.split(' ');

                                    const datePart = parts[0];
                                    const timePart = parts[1] || '09:00'; // default time

                                    const [day, month, year] = datePart.split('-');

                                    if (day && month && year) {
                                        avlblDateTime = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}`;
                                    }
                                }
                                // Add the target to the list
                                newSpecialTargets.push({
                                    contractorName: row[contractorNameIndex]?.trim() || '',
                                    contactNumber: contactNumber,
                                    companyName: row[companyNameIndex]?.trim() || '', // Use formatted contactNumber
                                    documentType: row[docTypeIndex]?.trim() || '',
                                    expiryDate: expiryDate,
                                    equipmentType: row[equipmentTypeIndex]?.trim() || '',
                                    avlblDateTime: avlblDateTime,
                                    suburbs: row[suburbsIndex]?.trim() || '',
                                });
                            }
                        }

                        // Update the special targets if any valid ones were found
                        if (newSpecialTargets.length > 0) {
                            this.specialTargets = newSpecialTargets; // Only assign if there are valid targets
                        } else {
                            if (invalidNumbers.length === lines.length - 1) { // All rows were invalid
                                alert('No valid target records found in the CSV for special customer. All contact numbers were invalid.');
                            } else {
                                alert('No valid target records found in the CSV for special customer after filtering invalid numbers.');
                            }
                        }
                    } else {
                        // For regular customers
                        const numberIndex = headers.indexOf('Target Number');

                        if (numberIndex === -1) {
                            alert("CSV must contain a 'Target Number' column."); // This alert is critical, should not be skipped.
                            return;
                        }

                        const newTargets = [];
                        let rowCounter = 1; // To track original row number for error messages
                        // Loop through each data row
                        for (let i = 1; i < lines.length; i++) {
                            rowCounter++;
                            const row = lines[i].split(',');
                            const originalTargetNumber = row[numberIndex]?.trim();
                            // Validate and format the phone number
                            const targetNumber = this.formatAndValidatePhoneNumber(originalTargetNumber);

                            if (targetNumber === null) {
                                invalidNumbers.push(`Row ${rowCounter}: '${originalTargetNumber}'`);
                                continue; // Skip this row if phone number is invalid
                            }

                            if (originalTargetNumber) { // Use originalTargetNumber for checking if it's not empty
                                // Build metadata from other columns
                                const botMetadata = {};
                                headers.forEach((header, index) => {
                                    if (index !== numberIndex && row[index]) {
                                        botMetadata[header] = row[index].trim().replace(/"/g, '');
                                    }
                                });

                                // Add the target
                                newTargets.push({
                                    targetNumber: targetNumber, // Use formatted targetNumber
                                    callingNumber: this.botDID || 'Anonymous',
                                    botMetadata: Object.keys(botMetadata).length > 0 ? JSON.stringify(botMetadata) : ''
                                });
                            }
                        }

                        // Update targets if any valid ones
                        if (newTargets.length > 0) {
                            this.targets = newTargets; // Only assign if there are valid targets
                        } else {
                            if (invalidNumbers.length === lines.length - 1) { // All rows were invalid
                                alert('No valid target records found in the CSV. All target numbers were invalid.');
                            } else {
                                alert('No valid target records found in the CSV after filtering invalid numbers.');
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error processing CSV file:", error);
                    alert("An error occurred while processing the CSV file. Please check the console for details.");
                }
            };
            reader.readAsText(file);
            reader.onloadend = () => {
                // After reading, show any invalid numbers that were skipped
                if (invalidNumbers.length > 0) {
                    alert(`The following phone numbers were invalid and have been skipped:\n${invalidNumbers.join('\n')}`);
                }
            };
        },
        // Adds a new empty special target row based on the bot type
        addSpecialTarget() {
            if (this.botName.includes('DB Re-engagement')) {
                this.specialTargets.push({ contractorName: '', contactNumber: '', companyName: '' });
            } else if (this.botName.includes('Expired Documentation')) {
                this.specialTargets.push({ contractorName: '', contactNumber: '', documentType: '', expiryDate: '' });
            } else if (this.botName.includes('Truck Availability')) {
                this.specialTargets.push({ contractorName: '', contactNumber: '', equipmentType: '', avlblDateTime: '', suburbs: '' });
            } else {
                this.specialTargets.push({});
            }
        },
        // Removes a special target row at the given index
        removeSpecialTarget(index) {
            this.specialTargets.splice(index, 1);
        },
        // Submits the special targets to the server for the campaign
        async submitSpecialTargets() {
            try {
                let targetsPayload;
                // Prepare payload based on bot type
                if (this.botName.includes('DB Re-engagement')) {
                    targetsPayload = this.specialTargets.map(t => ({
                        enabled: true,
                        targetNumber: t.contactNumber,
                        callingNumber: this.botDID || 'Anonymous',
                        botMetadata: JSON.stringify({ name: t.contractorName, company: t.companyName })
                    }));
                } else if (this.botName.includes('Expired Documentation')) {
                    targetsPayload = this.specialTargets.map(t => {
                        // Format the expiry date to a readable string
                        let formattedDate = t.expiryDate;
                        if (t.expiryDate) {
                            const [year, month, day] = t.expiryDate.split('-');
                            const dateObj = new Date(year, month - 1, day);
                            const m = dateObj.toLocaleString('default', { month: 'long' });
                            formattedDate = `${parseInt(day)} ${m} ${year}`;
                        }
                        return {
                            enabled: true,
                            targetNumber: t.contactNumber,
                            callingNumber: this.botDID || 'Anonymous',
                            botMetadata: JSON.stringify({ name: t.contractorName, document_type: t.documentType, expiry_date: formattedDate })
                        };
                    });
                } else if (this.botName.includes('Truck Availability')) {
                    targetsPayload = this.specialTargets.map(t => {
                        // Format the date/time back to dd-MM-yyyy HH:mm
                        let formattedDateTime = t.avlblDateTime;
                        if (t.avlblDateTime && t.avlblDateTime.includes('T')) {
                            const [datePart, timePart] = t.avlblDateTime.split('T');
                            const [year, month, day] = datePart.split('-');
                            formattedDateTime = `${day}-${month}-${year} ${timePart}`;
                        }
                        return {
                            enabled: true,
                            targetNumber: t.contactNumber,
                            callingNumber: this.botDID || 'Anonymous',
                            botMetadata: JSON.stringify({ name: t.contractorName, equipment_type: t.equipmentType, avlbl_date_time: formattedDateTime, suburbs: t.suburbs })
                        };
                    });
                } else {
                    targetsPayload = [];
                }
                // Prepare the full payload
                const payload = {
                    targets: targetsPayload
                };
                // Send to the server
                const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS;
                const url = `${baseUrl}/customers/${this.customerGUID}/campaign/${this.campaignId}/target`;

                const res = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });

                const responseData = await res.json();

                if (!res.ok) {
                    console.error("Error response:", responseData);
                    alert(`Failed to add targets: ${responseData.message || "Error"}`);
                    return;
                }

                alert("Targets added successfully!");
                // Go back to the outbound campaign tab
                this.goToOutboundCampaignTab();

            } catch (err) {
                console.error("Error adding targets:", err);
                alert("Failed to add targets. Check console for details.");
            }
        },



        // Submits the regular targets to the server
        async submitTargets() {
            try {
                // Prepare the payload with targets
                const payload = {
                    targets: this.targets.map(t => ({
                        enabled: true,
                        targetNumber: t.targetNumber.replace(/^tel:/, ''), // Remove tel: prefix if present
                        callingNumber: this.botDID || 'Anonymous',
                        botMetadata: t.botMetadata
                            ? (typeof t.botMetadata === "string"
                                ? t.botMetadata
                                : JSON.stringify(t.botMetadata))
                            : undefined
                    }))
                };
                // Send to the server
                const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS;
                const url = `${baseUrl}/customers/${this.customerGUID}/campaign/${this.campaignId}/target`;

                const res = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                const responseData = await res.json();
                if (!res.ok) {
                    console.error(" Error response:", responseData);
                    alert(`Failed: ${responseData.message || "Error adding targets"}`);
                    return;
                }

                alert("Targets added successfully!");
                // Go back to the outbound campaign tab
                this.goToOutboundCampaignTab();
            } catch (error) {
                console.error("Error adding targets:", error);
                alert("Failed to add targets. Check console for details.");
            }
        },


        // Navigates back to the outbound campaign tab
        goToOutboundCampaignTab() {
            this.$router.push({
                path: '/partner/customer',
                query: { activeTab: 'outboundCampaign' }
            });
        }
    }
}
</script>

<style scoped>
.Heading {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0 10px;
    margin-bottom: 30px;
}

.Heading h2 {
    margin: 0;
}

/* Wizard styles */
.wizard-steps {
    text-align: left;
    background-color: #fafafa;
    border-top: #e5e5e5 1px solid;
    border-bottom: #e5e5e5 1px solid;
    width: 95%;
    padding: 10px;
    color: #666;
    font-size: 16px;
    font-family: 'Montserrat';
    font-weight: bold;
    margin-bottom: 30px;
}

.num {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    background: #fff;
    margin-left: 10px;
    margin-right: 5px;
    border: 2px solid #e5e5e5;
    font-size: 15px;
    font-weight: 600;
    font-family: 'Montserrat', sans-serif;
}

.completed,
.current {
    display: inline-block;
}

.completed .step,
.completed .arrow {
    color: #42a593;
}

.current .step,
.current .arrow {
    color: #e45f2b;
}

.completed .num {
    background-color: #64CEBB;
    color: #fff;
    border: none;
}

.current .num {
    background-color: #FE602F;
    color: #fff;
    border: none;
}

.arrow {
    margin-left: 5px;
    font-size: 22px;
    font-weight: bolder;
}

/* CSV Upload Section */
.upload-section {
    padding: 10px 10px 25px 10px;
    font-family: 'Montserrat', sans-serif;
    text-align: left;
    margin-bottom: 20px;
    width: 95%;
    background-color: #f2f2f2;
    border: 1px solid #ddd;
    border-radius: 6px;
}

.upload-section h3 {
    font-size: 18px;
    color: #333;
    margin-bottom: 10px;
    font-weight: 600;
}

.csv-upload-input {
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background-color: #fff;
    width: 50%;
    margin-bottom: 8px;
}

.note {
    font-size: 13px;
    color: #555;
    margin-top: 6px;
    line-height: 1.4;
}

/* Target table */
.target-table-header {
    display: flex;
    background-color: #333c49;
    color: white;
    font-weight: bold;
    padding: 12px 10px;
    border-radius: 6px 6px 0 0;
    font-size: 16px;
}

.column {
    flex: 1;
    text-align: left;
}

.column-btn {
    width: 100px;
}

.target-table-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    margin-bottom: 12px;
}

.target-input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 14px;
}

.add-btn,
.remove-btn {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 10px 25px;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    font-weight: bold;
    font-size: 14px;
}

.add-btn {
    background-color: #E45F2B;
    color: white;
}

.add-btn:hover {
    background-color: #000;
}

.remove-btn {
    background-color: #788e90;
    color: white;
}

.remove-btn:hover {
    background-color: #5a686a;
}

.table-action-row {
    display: flex;
    justify-content: flex-start;
    padding-top: 12px;
    margin-bottom: 18px;
}

.add-round-btn {
    width: 42px !important;
    height: 42px !important;
    min-width: 42px !important;
    min-height: 42px !important;
    padding: 0 !important;
    border-radius: 50% !important;
    border: none !important;
    background-color: #E45F2B !important;
    color: white !important;
    font-size: 24px !important;
    font-weight: bold !important;
    line-height: 1 !important;
    cursor: pointer !important;
    display: inline-flex !important;
    justify-content: center !important;
    align-items: center !important;
}

.add-round-btn:hover {
    background-color: #d9501e;
}

.finish-button {
    background-color: #E45F2B;
    color: white;
    font-weight: bold;
}

.customization-note {
    clear: both;
    padding-top: 20px;
    font-size: 14px;
    color: #555;
    font-style: italic;
}

.customization-note a {
    color: #e45f2b;
    text-decoration: underline;
}

.guidelines-list {
    padding-left: 20px;
    margin-top: 5px;
}
</style>
